// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import { SCENARIO_TRANSLATIONS } from "../../utils/scenarioConfig";

const BlockAvailabilityForm = ({
  isVisible,
  editingPeriod,
  scenarios,
  onSave,
  onCancel,
  onNotification,
  mode,
}) => {
  const [formData, setFormData] = useState({
    scenario_id: "",
    occupied_from: "",
    occupied_to: "",
    slot_type: "full_day",
    description: "",
    start_hour: "",
    end_hour: "",
  });

  const scenarioRef = React.useRef(null);
  const occupiedFromRef = React.useRef(null);
  const occupiedToRef = React.useRef(null);
  const descriptionRef = React.useRef(null);

  const slotTypes = [
    { value: "full_day", label: "Ganztägig" },
    { value: "half_am", label: "Vormittag (AM)" },
    { value: "half_pm", label: "Nachmittag (PM)" },
    { value: "custom", label: "Benutzerdefiniert" },
  ];

  const scenarioTranslations = SCENARIO_TRANSLATIONS;

  const checkIsDeliveryScenario = React.useCallback(
    (scenarioId) => {
      if (!scenarioId) return false;
      const scenario = scenarios.find((s) => s.id === parseInt(scenarioId));
      return (
        scenario &&
        (scenario.name?.toLowerCase().includes("delivery") ||
          scenario.event_type?.toLowerCase() === "delivery" ||
          scenario.use_case?.toLowerCase() === "delivery")
      );
    },
    [scenarios],
  );

  useEffect(() => {
    if (editingPeriod) {
      const fromDate = new Date(editingPeriod.occupied_from);
      const toDate = new Date(editingPeriod.occupied_to);
      const isHourly = editingPeriod.slot_type === "hourly";
      // For delivery/hourly: date input needs YYYY-MM-DD (use local date to match list display)
      const toDateOnly = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const occupiedFromValue = isHourly
        ? toDateOnly(fromDate)
        : editingPeriod.occupied_from.slice(0, 16);
      const occupiedToValue = isHourly
        ? toDateOnly(toDate)
        : editingPeriod.occupied_to.slice(0, 16);

      setFormData({
        scenario_id: editingPeriod.scenario_id.toString(),
        occupied_from: occupiedFromValue,
        occupied_to: occupiedToValue,
        slot_type: editingPeriod.slot_type,
        description: editingPeriod.description,
        start_hour: isHourly ? fromDate.getHours().toString() : "",
        end_hour: isHourly ? toDate.getHours().toString() : "",
      });
    } else {
      setFormData({
        scenario_id: "",
        occupied_from: "",
        occupied_to: "",
        slot_type: "full_day",
        description: "",
        start_hour: "",
        end_hour: "",
      });
    }
  }, [editingPeriod, isVisible]);

  useEffect(() => {
    const isDelivery = checkIsDeliveryScenario(formData.scenario_id);

    if (
      isDelivery &&
      formData.occupied_from &&
      formData.start_hour !== "" &&
      formData.end_hour !== ""
    ) {
      const fromDate = new Date(formData.occupied_from);
      const toDate = new Date(formData.occupied_from);

      fromDate.setHours(parseInt(formData.start_hour), 0, 0, 0);
      toDate.setHours(parseInt(formData.end_hour), 59, 59, 999);
      if (parseInt(formData.end_hour) <= parseInt(formData.start_hour)) {
        toDate.setDate(toDate.getDate() + 1);
      }

      const newTo = toDate.toISOString();
      setFormData((prev) => ({
        ...prev,
        occupied_to: newTo,
      }));
    }
  }, [
    formData.scenario_id,
    formData.occupied_from,
    formData.start_hour,
    formData.end_hour,
    checkIsDeliveryScenario,
  ]);

  // Add state to track which fields have been touched
  const [touchedFields, setTouchedFields] = useState({
    scenario_id: false,
    occupied_from: false,
    occupied_to: false,
    description: false,
    start_hour: false,
    end_hour: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Mark field as touched
    setTouchedFields((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const scrollToElement = (element) => {
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      element.focus();
    }
  };

  // Add a function to mark fields as touched when validation fails
  const markFieldAsTouched = (fieldName) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  const validateForm = () => {
    // Check scenario selection
    if (!formData.scenario_id) {
      markFieldAsTouched("scenario_id");
      onNotification("Bitte wählen Sie ein Szenario aus", "error");
      scrollToElement(scenarioRef.current);
      return false;
    }

    const selectedScenario = scenarios.find(
      (s) => s.id === parseInt(formData.scenario_id),
    );
    const isDelivery =
      selectedScenario &&
      (selectedScenario.name?.toLowerCase().includes("delivery") ||
        selectedScenario.event_type?.toLowerCase() === "delivery" ||
        selectedScenario.use_case?.toLowerCase() === "delivery");

    // Check start date/time
    if (!formData.occupied_from) {
      markFieldAsTouched("occupied_from");
      onNotification(
        isDelivery
          ? "Bitte wählen Sie ein Datum aus"
          : "Bitte wählen Sie ein Startdatum und eine Startzeit aus",
        "error",
      );
      scrollToElement(occupiedFromRef.current);
      return false;
    }

    // For delivery scenarios, check hours
    if (isDelivery) {
      if (formData.start_hour === "") {
        markFieldAsTouched("start_hour");
        onNotification("Bitte wählen Sie eine Startzeit aus", "error");
        return false;
      }
      if (formData.end_hour === "") {
        markFieldAsTouched("end_hour");
        onNotification("Bitte wählen Sie eine Endzeit aus", "error");
        return false;
      }
    } else {
      // Check end date/time for non-delivery scenarios
      if (!formData.occupied_to) {
        markFieldAsTouched("occupied_to");
        onNotification(
          "Bitte wählen Sie ein Enddatum und eine Endzeit aus",
          "error",
        );
        scrollToElement(occupiedToRef.current);
        return false;
      }

      if (new Date(formData.occupied_from) >= new Date(formData.occupied_to)) {
        markFieldAsTouched("occupied_to");
        onNotification(
          "Enddatum/-zeit muss nach Startdatum/-zeit liegen",
          "error",
        );
        scrollToElement(occupiedToRef.current);
        return false;
      }
    }

    // Check description
    if (!formData.description.trim()) {
      markFieldAsTouched("description");
      onNotification("Bitte geben Sie eine Beschreibung ein", "error");
      scrollToElement(descriptionRef.current);
      return false;
    }

    return true;
  };

  // Update the getFieldErrorClass function to include red focus styling
  const getFieldErrorClass = (fieldName) => {
    const isTouched = touchedFields[fieldName];
    const isEmpty =
      fieldName === "scenario_id" ||
      fieldName === "start_hour" ||
      fieldName === "end_hour"
        ? !formData[fieldName]
        : !formData[fieldName] || !formData[fieldName].trim();

    return isTouched && isEmpty
      ? "border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20 dark:text-slate-200"
      : "border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:border-blue-500 focus:ring-blue-500 dark:placeholder-slate-400";
  };

  const getFieldErrorText = (fieldName) => {
    const isTouched = touchedFields[fieldName];
    const isEmpty =
      fieldName === "scenario_id" ||
      fieldName === "start_hour" ||
      fieldName === "end_hour"
        ? !formData[fieldName]
        : !formData[fieldName] || !formData[fieldName].trim();

    if (isTouched && isEmpty) {
      switch (fieldName) {
        case "scenario_id":
          return "Bitte wählen Sie ein Szenario aus";
        case "occupied_from":
          return isDeliveryScenario
            ? "Bitte wählen Sie ein Datum aus"
            : "Bitte wählen Sie ein Startdatum und eine Startzeit aus";
        case "occupied_to":
          return "Bitte wählen Sie ein Enddatum und eine Endzeit aus";
        case "description":
          return "Bitte geben Sie eine Beschreibung ein";
        case "start_hour":
          return "Bitte wählen Sie eine Startzeit aus";
        case "end_hour":
          return "Bitte wählen Sie eine Endzeit aus";
        default:
          return "";
      }
    }
    return "";
  };

  const getScenarioFullName = (scenario) => {
    const translatedName = scenarioTranslations[scenario.name] || scenario.name;
    return `${translatedName}${scenario.max_capacity ? ` (${scenario.max_capacity} Personen)` : ""}`;
  };

  // Check if selected scenario is a delivery scenario
  const isDeliveryScenario = checkIsDeliveryScenario(formData.scenario_id);

  // Generate hour options (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, "0")}:00`,
  }));

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const updateId = editingPeriod?.uuid || editingPeriod?.block_id;

      let occupiedFrom = formData.occupied_from;
      let occupiedTo = formData.occupied_to;
      let slotType = formData.slot_type;

      // For delivery scenarios, set slot_type to "hourly" and construct datetime from date + hours
      if (
        isDeliveryScenario &&
        formData.start_hour !== "" &&
        formData.end_hour !== ""
      ) {
        const fromDate = new Date(formData.occupied_from);
        const toDate = new Date(formData.occupied_from);

        fromDate.setHours(parseInt(formData.start_hour), 0, 0, 0);
        toDate.setHours(parseInt(formData.end_hour), 59, 59, 999);

        // If end hour is before or equal to start hour, assume it's next day
        if (parseInt(formData.end_hour) <= parseInt(formData.start_hour)) {
          toDate.setDate(toDate.getDate() + 1);
        }

        occupiedFrom = fromDate.toISOString();
        occupiedTo = toDate.toISOString();
        slotType = "hourly";
      } else {
        // For non-delivery scenarios, ensure dates are in ISO format
        if (formData.occupied_from && !formData.occupied_from.includes("T")) {
          occupiedFrom = new Date(
            formData.occupied_from + "T00:00:00",
          ).toISOString();
        }
        if (formData.occupied_to && !formData.occupied_to.includes("T")) {
          occupiedTo = new Date(
            formData.occupied_to + "T23:59:59",
          ).toISOString();
        }
      }

      const availabilityData = {
        scenario_id: parseInt(formData.scenario_id),
        occupied_from: occupiedFrom,
        occupied_to: occupiedTo,
        slot_type: slotType,
        description: formData.description.trim(),
      };

      await onSave(availabilityData, updateId);
    } catch (error) {
      console.error("Error in form submission:", error);
      onNotification("Fehler beim Speichern der Sperrzeit", "error");
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`w-1/2 transition-all duration-300 booking-details ${mode === "add" ? "w-full mx-auto" : "w-1/2"}`}
    >
      <div className="bg-white dark:bg-slate-800 shadow dark:border dark:border-slate-700 rounded-lg sticky top-6 overflow-y-auto">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-4">
            {editingPeriod
              ? "Sperrzeit bearbeiten"
              : "Neue Sperrzeit erstellen"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Szenario / Veranstaltungsort *
              </label>
              <select
                ref={scenarioRef}
                name="scenario_id"
                value={formData.scenario_id}
                onChange={handleInputChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 ${getFieldErrorClass("scenario_id")}`}
                required
              >
                <option value="">Szenario auswählen...</option>
                {scenarios.map((scenario) => (
                  <option key={scenario.id} value={scenario.id}>
                    {getScenarioFullName(scenario)}
                  </option>
                ))}
              </select>
              {getFieldErrorText("scenario_id") && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                  {getFieldErrorText("scenario_id")}
                </p>
              )}
              {formData.scenario_id && (
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  {
                    scenarios.find(
                      (s) => s.id === parseInt(formData.scenario_id),
                    )?.description
                  }
                </p>
              )}
            </div>

            {!isDeliveryScenario ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Zeitslot *
                </label>
                <select
                  name="slot_type"
                  value={formData.slot_type}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 ${getFieldErrorClass("slot_type")}`}
                >
                  {slotTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Startzeit (Stunde) *
                  </label>
                  <select
                    name="start_hour"
                    value={formData.start_hour}
                    onChange={handleInputChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 ${getFieldErrorClass("start_hour")}`}
                    required
                  >
                    <option value="">Stunde auswählen...</option>
                    {hourOptions.map((hour) => (
                      <option key={hour.value} value={hour.value}>
                        {hour.label}
                      </option>
                    ))}
                  </select>
                  {getFieldErrorText("start_hour") && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      {getFieldErrorText("start_hour")}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Endzeit (Stunde) *
                  </label>
                  <select
                    name="end_hour"
                    value={formData.end_hour}
                    onChange={handleInputChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 ${getFieldErrorClass("end_hour")}`}
                    required
                  >
                    <option value="">Stunde auswählen...</option>
                    {hourOptions.map((hour) => (
                      <option key={hour.value} value={hour.value}>
                        {hour.label}
                      </option>
                    ))}
                  </select>
                  {getFieldErrorText("end_hour") && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      {getFieldErrorText("end_hour")}
                    </p>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                {isDeliveryScenario ? "Datum *" : "Startdatum & -zeit *"}
              </label>
              <input
                ref={occupiedFromRef}
                type={isDeliveryScenario ? "date" : "datetime-local"}
                name="occupied_from"
                value={formData.occupied_from}
                onChange={handleInputChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 ${getFieldErrorClass("occupied_from")}`}
                required
              />
              {getFieldErrorText("occupied_from") && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                  {getFieldErrorText("occupied_from")}
                </p>
              )}
              {isDeliveryScenario && (
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Wählen Sie das Datum aus. Die genauen Stunden werden oben
                  ausgewählt.
                </p>
              )}
            </div>

            {!isDeliveryScenario && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Enddatum & -zeit *
                </label>
                <input
                  ref={occupiedToRef}
                  type="datetime-local"
                  name="occupied_to"
                  value={formData.occupied_to}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 ${getFieldErrorClass("occupied_to")}`}
                  required
                />
                {getFieldErrorText("occupied_to") && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                    {getFieldErrorText("occupied_to")}
                  </p>
                )}
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Beschreibung / Grund *
              </label>
              <textarea
                ref={descriptionRef}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="z.B. Jährliche Wartung, Ausstattungs-Upgrade, Aufbau für Sonderveranstaltung..."
                className={`w-full p-3 border rounded-lg focus:ring-2 ${getFieldErrorClass("description")}`}
                required
              />
              {getFieldErrorText("description") && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                  {getFieldErrorText("description")}
                </p>
              )}
            </div>

            <div className="md:col-span-2 flex gap-3 pt-4">
              <button
                onClick={handleSubmit}
                className="bg-primary-color text-white px-6 py-2 rounded-lg hover:opacity-90 transition-colors flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {editingPeriod
                  ? "Sperrzeit aktualisieren"
                  : "Sperrzeit erstellen"}
              </button>
              <button
                onClick={onCancel}
                className="flex items-center gap-2 px-4 py-2 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors disabled:bg-rose-100 dark:disabled:bg-rose-900/30 disabled:cursor-not-allowed"
              >
                <XCircle className="h-4 w-4" />
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockAvailabilityForm;
